import { type ReactNode } from 'react';
import * as stylex from '@stylexjs/stylex';
import { sentinel } from '../theme/semantic.stylex';

const styles = stylex.create({
  root: {
    display: 'grid',
    gap: '1rem',
    padding: '1.25rem 1.5rem',
    backgroundColor: sentinel.surfaceElevated,
    borderRadius: '0.625rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  heading: { display: 'grid', gap: '0.25rem', minWidth: 0 },
  title: {
    margin: 0,
    fontFamily: sentinel.fontDisplay,
    fontSize: '1.0625rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: sentinel.textBody,
    lineHeight: 1.3,
  },
  description: {
    margin: 0,
    fontFamily: sentinel.fontBody,
    fontSize: '0.875rem',
    color: sentinel.textMuted,
    lineHeight: 1.5,
  },
  actions: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  body: { display: 'grid', gap: '0.75rem' },
});

export interface DataSectionProps {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** Sets `data-testid` on the section root. */
  testId?: string;
}

export function DataSection({
  title,
  description,
  actions,
  children,
  testId,
}: DataSectionProps) {
  const hasHeader = title != null || description != null || actions != null;
  return (
    <section {...stylex.props(styles.root)} data-testid={testId}>
      {hasHeader ? (
        <header {...stylex.props(styles.header)}>
          <div {...stylex.props(styles.heading)}>
            {title ? (
              <h2
                {...stylex.props(styles.title)}
                data-testid={testId ? `${testId}-title` : undefined}
              >
                {title}
              </h2>
            ) : null}
            {description ? <p {...stylex.props(styles.description)}>{description}</p> : null}
          </div>
          {actions ? <div {...stylex.props(styles.actions)}>{actions}</div> : null}
        </header>
      ) : null}
      <div {...stylex.props(styles.body)}>{children}</div>
    </section>
  );
}
